import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Eye, Users, TrendingUp, Monitor, Smartphone, Tablet, Globe, MapPin } from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

const RANGES = [
  { key: 7, label: "7d" },
  { key: 30, label: "30d" },
  { key: 90, label: "90d" },
];

export default function AnalyticsSection() {
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  const [range, setRange] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = async (days = range) => {
    setLoading(true);
    try {
      const [sRes, tRes] = await Promise.all([
        api.get("/admin/analytics/summary"),
        api.get(`/admin/analytics/timeseries?days=${days}`),
      ]);
      setSummary(sRes.data);
      setSeries(tRes.data.series);
    } catch (e) {
      toast.error("Couldn't load analytics.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  if (loading && !summary) {
    return (
      <div data-testid="analytics-loading" className="p-16 text-center text-white/50 text-sm">
        Loading analytics…
      </div>
    );
  }
  if (!summary) return null;

  const t = summary.totals;
  const devices = summary.devices || {};
  const devTotal = Math.max(1, Object.values(devices).reduce((a, b) => a + b, 0));

  return (
    <section data-testid="analytics-section" className="mt-16">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#C9A24B] font-semibold mb-2">
            Website Analytics
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-bold">Traffic & Engagement</h2>
        </div>
        <div className="inline-flex bg-[#0B1B3D] border border-white/10" data-testid="analytics-range">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              data-testid={`range-${r.key}`}
              className={`px-4 py-2 text-xs uppercase tracking-[0.18em] font-semibold transition-colors ${
                range === r.key ? "bg-[#C9A24B] text-[#050B14]" : "text-white/70 hover:text-white"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <MetricCard icon={<Eye size={16} />} label="Views today"       value={t.views_today} testid="metric-views-today" />
        <MetricCard icon={<Eye size={16} />} label="Views last 7d"     value={t.views_7d}    testid="metric-views-7d" accent />
        <MetricCard icon={<Users size={16} />} label="Unique visitors 30d" value={t.uniques_30d} testid="metric-uniques-30d" />
        <MetricCard icon={<TrendingUp size={16} />} label="All-time views"  value={t.views_all}   testid="metric-views-all" />
      </div>

      <div className="bg-[#0B1B3D] border border-white/10 p-6 mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-white/60 font-semibold mb-6">
          Daily traffic — last {range} days
        </div>
        <div className="h-64" data-testid="analytics-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#C9A24B" stopOpacity={0.55} />
                  <stop offset="100%" stopColor="#C9A24B" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gUniques" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#9BD0E8" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#9BD0E8" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis
                dataKey="date"
                stroke="rgba(255,255,255,0.4)"
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => v.slice(5)}
              />
              <YAxis stroke="rgba(255,255,255,0.4)" tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  background: "#050B14",
                  border: "1px solid rgba(255,255,255,0.15)",
                  borderRadius: 0,
                  fontSize: 12,
                }}
                labelStyle={{ color: "#9BD0E8" }}
              />
              <Area type="monotone" dataKey="views" name="Views" stroke="#C9A24B" strokeWidth={2} fill="url(#gViews)" />
              <Area type="monotone" dataKey="uniques" name="Uniques" stroke="#9BD0E8" strokeWidth={1.5} fill="url(#gUniques)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="flex items-center gap-6 mt-4 text-xs text-white/70">
          <span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-[#C9A24B]" /> Page views</span>
          <span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-[#9BD0E8]" /> Unique visitors</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <ListCard
          title="Top cities"
          icon={<MapPin size={13} />}
          testid="top-cities"
          items={summary.top_cities}
          emptyLabel="Cities will appear once visitors browse from the internet."
        />
        <ListCard
          title="Top countries"
          icon={<Globe size={13} />}
          testid="top-countries"
          items={summary.top_countries}
        />
        <ListCard title="Top pages" testid="top-pages" items={summary.top_pages} formatter={(l) => l.length > 40 ? l.slice(0, 40) + "…" : l} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ListCard title="Top referrers" testid="top-referrers" items={summary.top_referrers.length ? summary.top_referrers : [{label: "(direct)", count: t.views_all}]} />
        <div className="bg-[#0B1B3D] border border-white/10 p-6 lg:col-span-2" data-testid="device-breakdown">
          <div className="text-xs uppercase tracking-[0.2em] text-white/60 font-semibold mb-6">Devices (30d)</div>
          {["desktop", "mobile", "tablet"].map((d) => {
            const c = devices[d] || 0;
            const pct = Math.round((c / devTotal) * 100);
            const Icon = d === "desktop" ? Monitor : d === "mobile" ? Smartphone : Tablet;
            return (
              <div key={d} className="mb-5 last:mb-0" data-testid={`device-${d}`}>
                <div className="flex items-center justify-between text-sm mb-2">
                  <div className="flex items-center gap-2 capitalize"><Icon size={13} className="text-[#9BD0E8]" />{d}</div>
                  <div className="text-white/60 text-xs">{c} · {pct}%</div>
                </div>
                <div className="h-1.5 bg-white/5 overflow-hidden">
                  <div className="h-full bg-[#C9A24B]" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function MetricCard({ icon, label, value, testid, accent }) {
  return (
    <div
      data-testid={testid}
      className={`border p-5 ${accent ? "bg-[#C9A24B]/10 border-[#C9A24B]/30" : "bg-[#0B1B3D] border-white/10"}`}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/60 font-semibold mb-3">
        <span className="text-[#C9A24B]">{icon}</span>
        {label}
      </div>
      <div className={`font-display text-3xl font-bold ${accent ? "text-[#C9A24B]" : "text-white"}`}>{value}</div>
    </div>
  );
}

function ListCard({ title, items, testid, formatter, icon, emptyLabel }) {
  return (
    <div className="bg-[#0B1B3D] border border-white/10 p-6" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/60 font-semibold mb-4">
        {icon && <span className="text-[#C9A24B]">{icon}</span>}
        {title}
      </div>
      {(!items || items.length === 0) ? (
        <div className="text-sm text-white/40">{emptyLabel || "No data yet."}</div>
      ) : (
        <ul className="space-y-3">
          {items.map((i, idx) => (
            <li key={idx} className="flex items-center justify-between gap-3 text-sm">
              <span className="text-white/80 truncate">{formatter ? formatter(i.label) : i.label}</span>
              <span className="text-[#C9A24B] font-semibold text-xs">{i.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
